class CountryOverlayGate extends HTMLElement {
  constructor() {
    super();

    this.storageKey = 'fade:country-overlay-gate';
    this.handleFocusIn = this.onFocusIn.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleDomMutation = this.onDomMutation.bind(this);
    this.handleAttributeMutation = this.onAttributeMutation.bind(this);
  }

  connectedCallback() {
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.dialog = this.querySelector('[data-country-gate-dialog]');

    const configElement = this.querySelector('[data-country-gate-config]');
    if (!configElement) {
      return;
    }

    try {
      this.config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Country gate configuration is invalid.', error);
      return;
    }

    if (!this.config.enabled || this.config.designMode) {
      return;
    }

    this.blockedCountries = this.parseBlockedCountries(this.config.blockedCountriesText);
    if (!this.blockedCountries.length) {
      return;
    }

    const cachedState = this.getStoredState();
    if (cachedState && cachedState.version === this.getConfigVersion()) {
      this.applyState(cachedState);

      return;
    }

    this.fetchVisitorLocation();
  }

  disconnectedCallback() {
    this.deactivateOverlay();
  }

  async fetchVisitorLocation() {
    if (!this.config.apiKey) {
      console.warn('Country gate API key is missing.');
      return;
    }

    try {
      const response = await fetch(this.config.apiUrl, {
        headers: {
          'Accept': 'application/json',
          'x-api-key': this.config.apiKey
        }
      });

      if (!response.ok) {
        throw new Error(`Country gate request failed with status ${response.status}.`);
      }

      const payload = await response.json();
      if (!payload.success || !payload.data) {
        throw new Error('Country gate response did not include location data.');
      }

      const state = {
        blocked: this.isBlockedCountry(payload.data),
        country: payload.data.country || '',
        countryCode: payload.data.countryCode || '',
        version: this.getConfigVersion()
      };

      this.setStoredState(state);
      this.applyState(state);
    } catch (error) {
      console.error('Country gate could not determine the visitor country.', error);
    }
  }

  applyState(state) {
    if (!state || !state.blocked) {
      this.deactivateOverlay();
      return;
    }

    if (!this.config.isHomepage) {
      this.redirectToHomepage();
      return;
    }

    this.activateOverlay();
  }

  activateOverlay() {
    if (this.isActive) {
      return;
    }

    this.isActive = true;
    this.hidden = false;
    document.body.classList.add('country-gate-open');
    document.addEventListener('focusin', this.handleFocusIn, true);
    document.addEventListener('keydown', this.handleKeydown, true);
    this.ensureTamperProtection();
    this.restoreOverlayVisibility();

    const focusTarget = this.getFocusableElements()[0] || this.dialog;
    if (focusTarget) {
      focusTarget.focus();
    }
  }

  deactivateOverlay() {
    this.removeTamperProtection();
    if (!this.isActive) {
      this.hidden = true;
      return;
    }

    this.isActive = false;
    this.hidden = true;
    document.body.classList.remove('country-gate-open');
    document.removeEventListener('focusin', this.handleFocusIn, true);
    document.removeEventListener('keydown', this.handleKeydown, true);
  }

  ensureTamperProtection() {
    if (this.tamperObserver) {
      return;
    }

    if (!this.originalParent) {
      this.originalParent = this.parentElement;
      this.originalNextSibling = this.nextSibling;
    }

    this.tamperObserver = new MutationObserver(this.handleDomMutation);
    this.tamperObserver.observe(document.body, { childList: true, subtree: true });

    this.attributeObserver = new MutationObserver(this.handleAttributeMutation);
    this.attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: ['hidden', 'style', 'class']
    });
  }

  removeTamperProtection() {
    if (this.tamperObserver) {
      this.tamperObserver.disconnect();
      this.tamperObserver = null;
    }

    if (this.attributeObserver) {
      this.attributeObserver.disconnect();
      this.attributeObserver = null;
    }
  }

  onDomMutation() {
    if (!this.isActive) {
      return;
    }

    if (!document.body.contains(this)) {
      const parent = this.originalParent || document.body;
      if (this.originalNextSibling && this.originalNextSibling.parentNode === parent) {
        parent.insertBefore(this, this.originalNextSibling);
      } else {
        parent.appendChild(this);
      }
    }

    this.restoreOverlayVisibility();
  }

  onAttributeMutation() {
    if (!this.isActive) {
      return;
    }

    this.restoreOverlayVisibility();
  }

  restoreOverlayVisibility() {
    if (this.hidden) {
      this.hidden = false;
    }

    if (this.style.display === 'none') {
      this.style.display = '';
    }

    if (this.style.visibility === 'hidden') {
      this.style.visibility = '';
    }

    document.body.classList.add('country-gate-open');
  }

  redirectToHomepage() {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(this.config.homeUrl, window.location.origin);
    const paramsToKeep = ['preview_theme_id', '_fd', 'pb', 'preview_script_id'];

    paramsToKeep.forEach((param) => {
      const value = currentUrl.searchParams.get(param);
      if (value) {
        targetUrl.searchParams.set(param, value);
      }
    });

    if (targetUrl.href !== currentUrl.href) {
      window.location.replace(targetUrl.toString());
    }
  }

  onFocusIn(event) {
    if (!this.isActive || this.contains(event.target)) {
      return;
    }

    const focusTarget = this.getFocusableElements()[0] || this.dialog;
    if (focusTarget) {
      focusTarget.focus();
    }
  }

  onKeydown(event) {
    if (!this.isActive || event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.dialog && this.dialog.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === firstElement || activeElement === this.dialog)) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
      return;
    }

    if (!this.contains(activeElement)) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  getFocusableElements() {
    return [...this.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => {
      return !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  parseBlockedCountries(blockedCountriesText) {
    return blockedCountriesText
      .split(/[\n,;]+/)
      .map((country) => this.normalizeCountry(country))
      .filter(Boolean);
  }

  isBlockedCountry(locationData) {
    const country = this.normalizeCountry(locationData.country || '');
    const countryCode = this.normalizeCountry(locationData.countryCode || '');

    return this.blockedCountries.includes(country) || this.blockedCountries.includes(countryCode);
  }

  normalizeCountry(value) {
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  getConfigVersion() {
    return `${this.config.apiUrl}|${this.config.blockedCountriesText}`;
  }

  getStoredState() {
    try {
      const rawState = sessionStorage.getItem(this.storageKey);
      if (!rawState) {
        return null;
      }

      return JSON.parse(rawState);
    } catch (error) {
      console.error('Country gate cache could not be read.', error);
      return null;
    }
  }

  setStoredState(state) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Country gate cache could not be written.', error);
    }
  }
}

if (!customElements.get('country-overlay-gate')) {
  customElements.define('country-overlay-gate', CountryOverlayGate);
}